import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { use, useEffect, useState, useCallback } from 'react';
import { Button, Carousel, Container, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useParams } from 'react-router';

import LotCountContext from '../../Contexts/LotCountContext';
import { Dictionary } from '../../Types/Dictionary';
import { Lot } from '../../Types/Lot';
import { useGetLotDetails } from '../../Utilities/useLotDetails';
import { LotDetailsSelector } from './LotDetailsSelector';
import LotDetailPageWrapper from './LotDetailsPageWrapper';
import MissingLotPage from './MissingLotPage';

export function LotDetailsPage() {
    const { lotNumber: initialLotNumber } = useParams<{ lotNumber: string; }>();
    const { lotList, setLotList } = use(LotCountContext);
    const [activeLotIndex, setActiveLotIndex] = useState<number>(lotList.findIndex(x => x === initialLotNumber));

    const [loading, setLoading] = useState<boolean>(false);

    // UseEffect to catch forward and back navigation. 
    // We've lied to react router to allow us to have smooth page transitions so we can't rely on react router's params hook to track lot numbers 
    useEffect(() => {
        const listener = window.addEventListener("popstate", (event) => {
            const pathName = window.location.pathname;
            if (!pathName.startsWith("/lotdetails")) {
                return;
            }

            const parts = pathName.split("/");
            if (parts.length !== 3) {
                return;
            }

            const lotNumber = parts[2];
            setActiveLotIndex(lotList.findIndex(x => x === lotNumber));
        });

        return listener;
    }, [lotList]);

    const { getLotDetails } = useGetLotDetails();

    const [lotDetailsDictionary, setLotDetailsDictionary] = useState<Dictionary<Lot>>({});

    const fetchCurrentAndNeighbouringLots = useCallback((selectedIndex: number) => {
        let currentLotNumber = selectedIndex === -1
            ? initialLotNumber ?? ""
            : lotList[selectedIndex];
        let promises = [];
        if (lotDetailsDictionary[currentLotNumber] === undefined) {
            promises.push(getLotDetails(currentLotNumber));
        }

        if (lotList.length > 0) {
            if (selectedIndex !== 0) {
                let previousLotNumber = lotList[selectedIndex - 1];
                if (lotDetailsDictionary[previousLotNumber] === undefined) {
                    promises.push(getLotDetails(previousLotNumber));
                }
            }

            if (selectedIndex !== lotList.length - 1) {
                let nextLotNumber = lotList[selectedIndex + 1];
                if (lotDetailsDictionary[nextLotNumber] === undefined) {
                    promises.push(getLotDetails(nextLotNumber));
                }
            }
        }

        if (promises.length > 0) {
            setLoading(true);
            Promise.all(promises)
                .then((lots) => {
                    setLotDetailsDictionary((prev) => {
                        let dictionary = prev;
                        lots.forEach(l => {
                            if ((typeof (l) === 'string') || (typeof (l) === 'number')) {
                                return;
                            }
                            dictionary[l.summary.lotNumber] = l;
                        });

                        return dictionary;
                    });
                    setLoading(false);
                });
        }
    }, [getLotDetails, initialLotNumber, lotDetailsDictionary, lotList]);

    useEffect(() => {
        fetchCurrentAndNeighbouringLots(activeLotIndex);
    }, [activeLotIndex, fetchCurrentAndNeighbouringLots]);

    const handleSlideChange = (selectedIndex: number) => {
        // Alter the URL without triggering a proper react-router event
        const newUrl = `/lotdetails/${lotList[selectedIndex]}`;
        window.history.pushState({}, "", newUrl);
        setActiveLotIndex(selectedIndex);

        fetchCurrentAndNeighbouringLots(selectedIndex);
    };

    /**
    * Called when a lot is renamed to update the dictionary with the new lot number
    * Removes the old lot number entry and adds the new one
    */
    const handleLotRenamed = useCallback((oldLotNumber: string, newLotNumber: string, updatedLot: Lot) => {
        setLotList((prev) => prev.map(lotNumber => lotNumber === oldLotNumber ? newLotNumber : lotNumber));
        setLotDetailsDictionary((prev) => {
            const updated = { ...prev };
            updated[newLotNumber] = updatedLot;

            delete updated[oldLotNumber];
            return updated;
        });
    }, [setLotList]);

    const currentLot = lotDetailsDictionary[initialLotNumber ?? ""];

    if (!loading && currentLot === undefined) {
        return <MissingLotPage />;
    }

    return (
        <Container fluid>
            {lotList.length === 0 // We have an empty lot list page on page refreshes so need to handle this case
                ? <LotDetailPageWrapper initialLot={currentLot} active={true}>
                    <LotDetailsSelector businessUnitCode={currentLot?.summary?.businessUnitCode} lotNumber={initialLotNumber ?? ""} />
                </LotDetailPageWrapper>
                : <>
                    <Carousel
                        activeIndex={activeLotIndex}
                        onSelect={handleSlideChange}
                        interval={null}
                        indicators={false}
                        controls={false}
                        wrap={false}

                    >
                        {lotList.map((lotNumber, index) =>
                            <Carousel.Item key={`${index}-slide`}>
                                <LotDetailPageWrapper initialLot={lotDetailsDictionary[lotNumber]} active={index === activeLotIndex} onLotRenamed={handleLotRenamed}>
                                    <LotDetailsSelector businessUnitCode={lotDetailsDictionary[lotNumber]?.summary?.businessUnitCode} lotNumber={lotNumber} />
                                </LotDetailPageWrapper>
                            </Carousel.Item>
                        )}
                    </Carousel>
                    {activeLotIndex !== 0 && (
                        <OverlayTrigger
                            placement="right"
                            overlay={(props) => (
                                <Tooltip id="button-tooltip" {...props}>
                                    {lotList[activeLotIndex - 1]}
                                </Tooltip>
                            )}
                        >
                            <Button className={`m-2 position-fixed top-50 start-0`} onClick={() => handleSlideChange(activeLotIndex - 1)}>
                                <FontAwesomeIcon icon={faChevronLeft} size={"xl"} />
                            </Button>
                        </OverlayTrigger>
                    )}
                    {activeLotIndex !== lotList.length - 1 && (
                        <OverlayTrigger
                            placement="left"
                            overlay={(props) => (
                                <Tooltip id="button-tooltip" {...props}>
                                    {lotList[activeLotIndex + 1]}
                                </Tooltip>
                            )}
                        >
                            <Button className={`m-2 position-fixed top-50 end-0`} onClick={() => handleSlideChange(activeLotIndex + 1)}>
                                <FontAwesomeIcon icon={faChevronRight} size={"xl"} />
                            </Button>
                        </OverlayTrigger>
                    )}
                </>
            }
        </Container>
    );
}

export default LotDetailsPage;